import { useState, useEffect, useRef } from 'react';
import type { GameType, RoomMode } from '@big2/shared';
import { useGameState } from '../net/GameProvider.js';
import { useI18n } from '../i18n.js';
import { applyPageSeo, type InfoPageType } from '../utils/seo.js';
import { trackEvent } from '../analytics.js';
import { playMatchFoundSound } from '../sound.js';
import { LandingHero } from './landing/LandingHero.js';
import { RoomLauncher } from './landing/RoomLauncher.js';
import { MatchmakingBanner } from './landing/MatchmakingBanner.js';
import { LandingGamesShowcase } from './landing/LandingGamesShowcase.js';
import { LandingHowToPlay } from './landing/LandingHowToPlay.js';
import { LandingFeatures } from './landing/LandingFeatures.js';
import { LandingFaq } from './landing/LandingFaq.js';
import { LandingFooter } from './landing/LandingFooter.js';
import './landing.css';

export interface WelcomeScreenProps {
  initialGameType?: GameType;
  onOpenRules?: (gameType?: GameType) => void;
  onOpenCardBacks?: () => void;
  onOpenProfile?: () => void;
  onOpenInfoPage?: (page: InfoPageType) => void;
}

export function WelcomeScreen({
  initialGameType,
  onOpenRules,
  onOpenCardBacks,
  onOpenProfile,
  onOpenInfoPage,
}: WelcomeScreenProps) {
  const { state, actions } = useGameState();
  const { lang } = useI18n();
  const isMn = lang === 'mn';

  const [name, setName] = useState(() => {
    if (state.profile?.displayName) return state.profile.displayName;
    const saved = typeof localStorage !== 'undefined' ? localStorage.getItem('big2.name') : null;
    if (saved && saved.trim()) return saved.trim();
    const guestName = `Guest-${Math.floor(1000 + Math.random() * 9000)}`;
    try {
      if (typeof localStorage !== 'undefined') localStorage.setItem('big2.name', guestName);
    } catch {}
    return guestName;
  });

  useEffect(() => {
    if (state.profile?.displayName && state.profile.displayName !== name) {
      setName(state.profile.displayName);
    }
  }, [state.profile?.displayName]);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [gameType, setGameType] = useState<GameType>(() => {
    if (initialGameType && (initialGameType !== 'twentyone' || state.supportedGames.includes('twentyone'))) return initialGameType;
    const p = typeof window !== 'undefined' ? window.location.pathname.toLowerCase() : '';
    if (p.includes('daaluu')) return 'daaluu';
    if (state.supportedGames.includes('twentyone') && (p.includes('twentyone') || p.includes('21') || p.includes('ochko'))) return 'twentyone';
    if (p.includes('muushig')) return 'muushig';
    return 'big2';
  });

  useEffect(() => {
    if (initialGameType && initialGameType !== gameType) {
      setGameType(initialGameType);
    }
  }, [initialGameType]);
  const [mode, setMode] = useState<RoomMode>('free');
  const [customRoomName, setCustomRoomName] = useState('');
  const [voiceChatEnabled, setVoiceChatEnabled] = useState(false);
  const [joinCode, setJoinCode] = useState(() =>
    new URLSearchParams(window.location.search).get('room')?.trim().toUpperCase().slice(0, 20) ?? ''
  );
  const [localStatus, setLocalStatus] = useState<'searching' | 'found' | 'timeout' | null>(null);
  const [localElapsedSec, setLocalElapsedSec] = useState(0);

  const matchingRoomsCount = state.publicRooms.filter(
    (r) => (r.gameType ?? 'big2') === gameType && r.mode === mode,
  ).length;

  const activeStatus = state.matchmaking?.status && state.matchmaking.status !== 'cancelled'
    ? state.matchmaking.status
    : localStatus;
  const timeoutSec = state.matchmaking?.timeoutSeconds ?? 20;
  const elapsedSec = Math.max(localElapsedSec, state.matchmaking?.elapsedSeconds ?? 0);
  const estimatedSec =
    state.matchmaking?.estimatedSeconds ??
    (matchingRoomsCount > 0 ? 1 : Math.round(timeoutSec * 0.5));
  const playersInQueue = state.matchmaking?.playersInQueue ?? 1;

  useEffect(() => {
    if (state.matchmaking?.status === 'timeout') {
      setLocalStatus('timeout');
    } else if (state.matchmaking?.status === 'found') {
      setLocalStatus('found');
      playMatchFoundSound();
    } else if (state.matchmaking?.status === 'cancelled') {
      setLocalStatus(null);
      setLocalElapsedSec(0);
    }
  }, [state.matchmaking?.status]);

  useEffect(() => {
    if (activeStatus !== 'searching') return;
    const tick = setInterval(() => {
      setLocalElapsedSec((prev) => {
        const next = prev + 1;
        if (next >= timeoutSec) {
          setLocalStatus('timeout');
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [activeStatus, timeoutSec]);

  useEffect(() => {
    if (state.profile?.displayName) setName(state.profile.displayName);
  }, [state.profile?.displayName]);

  useEffect(() => {
    if (window.location.search.includes('room=')) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    const p = window.location.pathname.toLowerCase().replace(/\/+$/, '') || '/';
    if (
      p === '/privacy' ||
      p === '/terms' ||
      p === '/subscription' ||
      p === '/vip' ||
      p === '/admin' ||
      p.startsWith('/rules') ||
      p === '/how-to-play' ||
      p.endsWith('/rules')
    ) {
      return;
    }
    const homeBreadcrumb = { name: isMn ? 'Нүүр' : 'Home', path: '/' };
    if (p === '/daaluu') {
      applyPageSeo({
        title: isMn ? 'Даалуу — Онлайн тоглоом | arvangurav.com' : 'Daaluu — Play Online | arvangurav.com',
        description: isMn ? 'Найзуудтайгаа Даалуу тоглож, модоо ширээн дээр холиод цай хураагаарай.' : 'Play Daaluu with friends, mix tiles together on the table, and collect room tea.',
        canonicalPath: '/daaluu',
        breadcrumbs: [homeBreadcrumb, { name: isMn ? 'Монгол Даалуу' : 'Daaluu', path: '/daaluu' }],
      });
    } else if (p === '/twentyone' || p === '/21' || p === '/ochko') {
      applyPageSeo({
        title: isMn
          ? '21 (Очко) – Монгол Онлайн Хөзрийн Тоглоом | arvangurav.com'
          : 'Twenty-One (21 / Ochko) – Play Online | arvangurav.com',
        description: isMn
          ? 'Монголын уламжлалт 21 (Очко) 36 хөзрийн тоглоомыг онлайнаар найзуудтайгаа эсвэл боттой үнэгүй тоглоорой. Банк цохих, Алтан 21 (Хоёр Туз), бооцоо тавих дүрмүүдтэй.'
          : 'Play traditional Mongolian 21 (Ochko) online with friends or bots. 36-card deck, Golden 21, betting, and bank mechanics.',
        canonicalPath: '/twentyone',
        breadcrumbs: [homeBreadcrumb, { name: isMn ? '21 (Очко)' : '21 (Ochko)', path: '/twentyone' }],
      });
    } else if (p === '/muushig') {
      applyPageSeo({
        title: isMn
          ? 'Муушиг – Монгол Онлайн Хөзрийн Тоглоом | arvangurav.com'
          : 'Muushig – Play Mongolian Card Game Online | arvangurav.com',
        description: isMn
          ? 'Монголын уламжлалт Муушиг чөлөөт цагийн хөзрийн тоглоомыг 2–5 тоглогчоор онлайнаар найзуудтайгаа эсвэл боттой тоглоорой. Хөзөр солих, өнжих, гэр авах, 15 онооноос 0 болгож ялах албан ёсны дүрэмтэй.'
          : 'Play traditional Mongolian Muushig casual card game online with 2–5 players or bots. Classic 32-card deck, trump suit, card exchange, folding, and 15-to-0 scoring.',
        canonicalPath: '/muushig',
        breadcrumbs: [homeBreadcrumb, { name: isMn ? 'Муушиг' : 'Muushig', path: '/muushig' }],
      });
    } else {
      const isBig2Explicit = p === '/big2' || p === '/arvan-gurav';
      applyPageSeo({
        title: isMn
          ? 'Арван гурав – Онлайн Хөзрийн Тоглоом | arvangurav.com'
          : 'Big Two (Arvan Gurav) – Play Online Card Game | arvangurav.com',
        description: isMn
          ? 'Монголын #1 Арван гурав (Хоёр) онлайн хөзрийн тоглоомын төв — 13 модоо хамгийн түрүүнд гаргаж ял! Мөн Муушиг, 21 (Очко), Даалуу зэрэг нэмэлт тоглоомуудыг найзуудтайгаа эсвэл боттой үнэгүй тоглоорой.'
          : 'Play Big Two (Arvan Gurav / Deuces / Pusoy Dos) online with friends or bots for free at arvangurav.com — plus extra traditional games including Muushig, 21 (Ochko), and Daaluu.',
        canonicalPath: isBig2Explicit ? '/big2' : '/',
        breadcrumbs: isBig2Explicit ? [homeBreadcrumb, { name: isMn ? 'Арван гурав (Big Two)' : 'Big Two', path: '/big2' }] : undefined,
      });
    }
  }, [isMn]);

  const selectGameType = (next: GameType) => {
    if (next === 'daaluu') setMode('free');
    setGameType(next);
    trackEvent('game_type_selected', { game_type: next, source: 'welcome' });
    trackEvent(`game_selected_${next}`, { source: 'welcome' });
    const p = window.location.pathname.toLowerCase().replace(/\/+$/, '');
    if (p === '/muushig' || p === '/big2' || p === '/arvan-gurav' || p === '/twentyone' || p === '/21' || p === '/ochko') {
      window.history.pushState({}, '', next === 'twentyone' ? '/twentyone' : next === 'muushig' ? '/muushig' : '/big2');
    }
  };

  const scrollToLauncher = (targetGame?: GameType) => {
    if (targetGame) selectGameType(targetGame);
    const el = document.getElementById('room-launcher');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => {
        nameInputRef.current?.focus();
      }, 350);
    }
  };

  const isPaid = state.profile?.tier === 'paid';

  const handleCreate = (options?: { customName?: string; voiceChat?: boolean }) => {
    const finalName = (name.trim() || state.profile?.displayName || '').trim();
    if (!finalName) return;
    if (mode === 'tsai' && !state.profile && onOpenProfile) {
      onOpenProfile();
      return;
    }
    localStorage.setItem('big2.name', finalName);
    const finalCustomName = options?.customName !== undefined ? options.customName : customRoomName;
    const finalVoiceChat = options?.voiceChat !== undefined ? options.voiceChat : voiceChatEnabled;
    actions.createRoom(
      finalName,
      mode,
      isPaid && finalCustomName.trim() ? finalCustomName.trim() : undefined,
      gameType,
      isPaid && finalVoiceChat ? true : undefined,
      false,
    );
  };

  const handleQuickPlay = () => {
    const finalName = (name.trim() || state.profile?.displayName || '').trim();
    if (!finalName) return;
    if (mode === 'tsai' && !state.profile && onOpenProfile) {
      onOpenProfile();
      return;
    }
    localStorage.setItem('big2.name', finalName);
    setLocalElapsedSec(0);
    setLocalStatus('searching');
    actions.quickPlay(finalName, gameType, mode, true);
  };

  const handleCancelMatchmaking = () => {
    setLocalStatus(null);
    setLocalElapsedSec(0);
    actions.cancelMatchmaking?.();
  };

  const handleJoin = (targetCode?: string) => {
    const finalName = (name.trim() || state.profile?.displayName || (isMn ? 'Тоглогч' : 'Player')).trim();
    const effectiveCode = (targetCode ?? joinCode).trim().toUpperCase();
    if (!finalName || !effectiveCode) return;
    localStorage.setItem('big2.name', finalName);
    actions.joinRoom(effectiveCode, finalName);
  };

  return (
    <div className="landing-page">
      {activeStatus && (
        <MatchmakingBanner
          status={activeStatus}
          elapsedSeconds={elapsedSec}
          estimatedSeconds={estimatedSec}
          timeoutSeconds={timeoutSec}
          playersInQueue={playersInQueue}
          gameType={gameType}
          mode={mode}
          matchingRoomsCount={matchingRoomsCount}
          onCancel={handleCancelMatchmaking}
          onCreateRoom={() => {
            handleCancelMatchmaking();
            handleCreate();
          }}
          onRetrySearch={handleQuickPlay}
        />
      )}
      <LandingHero>
        <RoomLauncher
          nameInputRef={nameInputRef}
          name={name}
          setName={setName}
          gameType={gameType}
          selectGameType={selectGameType}
          mode={mode}
          setMode={setMode}
          customRoomName={customRoomName}
          setCustomRoomName={setCustomRoomName}
          voiceChatEnabled={voiceChatEnabled}
          setVoiceChatEnabled={setVoiceChatEnabled}
          joinCode={joinCode}
          setJoinCode={setJoinCode}
          handleQuickPlay={handleQuickPlay}
          handleCreate={handleCreate}
          handleJoin={handleJoin}
          matchmakingStatus={activeStatus}
          matchmakingElapsedSec={elapsedSec}
          matchmakingEstimatedSec={estimatedSec}
          onCancelMatchmaking={handleCancelMatchmaking}
          onOpenCardBacks={onOpenCardBacks}
          onOpenProfile={onOpenProfile}
          onOpenRules={onOpenRules}
        />
      </LandingHero>
      <LandingGamesShowcase
        onScrollToLauncher={scrollToLauncher}
        onOpenRules={onOpenRules}
      />
      <LandingHowToPlay />
      <LandingFeatures />
      <LandingFaq />
      <LandingFooter
        onScrollToLauncher={scrollToLauncher}
        onOpenRules={onOpenRules}
        onOpenInfoPage={onOpenInfoPage}
      />
    </div>
  );
}
